import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { dashboardService } from '../../services/api';

const chartColors = ['#0E3A8C', '#3B82F6', '#FF6A3D', '#FF8A00', '#0B1F3B', '#6B7280'];

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value || 0);
const formatTokensMillions = (value) => {
  const numericValue = Number(value || 0) / 1_000_000;
  return `${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(numericValue)}M`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Number(value || 0));

const formatDateLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const buildParams = (filters) => {
  const params = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      params[key] = value;
    }
  });
  return params;
};

const AdminDashboard = () => {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userId: '',
    projectId: '',
    folderId: '',
    modelName: '',
    isCrossProject: ''
  });

  const [timeMetric, setTimeMetric] = useState('tokens');
  const [granularity, setGranularity] = useState('day');
  const [refreshToken, setRefreshToken] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [overview, setOverview] = useState(null);
  const [timeSeries, setTimeSeries] = useState({ series: [] });
  const [modelBreakdown, setModelBreakdown] = useState({ items: [] });
  const [userBreakdown, setUserBreakdown] = useState({ items: [] });
  const [projectBreakdown, setProjectBreakdown] = useState({ items: [] });
  const [conversations, setConversations] = useState({ items: [] });
  const [filterOptions, setFilterOptions] = useState({ users: [], projects: [], folders: [], models: [] });
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => buildParams(filters), [filters]);

  useEffect(() => {
    dashboardService.getFilters()
      .then((response) => setFilterOptions(response.data))
      .catch((error) => console.error('Failed to load dashboard filters:', error));
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, timeRes, modelRes, userRes, projectRes, conversationsRes] = await Promise.all([
        dashboardService.getOverview(params),
        dashboardService.getTimeSeries({ ...params, metric: timeMetric, granularity }),
        dashboardService.getBreakdown('model', params),
        dashboardService.getBreakdown('user', params),
        dashboardService.getBreakdown('project', params),
        dashboardService.getConversations({ ...params, page: 1, pageSize: 15 })
      ]);

      setOverview(overviewRes.data);
      setTimeSeries(timeRes.data);
      setModelBreakdown(modelRes.data);
      setUserBreakdown(userRes.data);
      setProjectBreakdown(projectRes.data);
      setConversations(conversationsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [params, timeMetric, granularity]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshToken]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key] === value ? '' : value
    }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      userId: '',
      projectId: '',
      folderId: '',
      modelName: '',
      isCrossProject: ''
    });
  };

  const overviewTotals = overview?.totals || {};
  const activeFilterCount = Object.values(filters).filter((value) => value).length;
  const modelMixData = modelBreakdown.items.map((item) => ({
    ...item,
    totalTokens: Number(item.totalTokens || 0)
  }));
  const hasModelMixData = modelMixData.some((item) => item.totalTokens > 0);

  return (
    <div className="flex gap-4 pb-10 relative">
      <div className="flex-1 flex flex-col gap-6 min-w-0">
        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard title="Total Conversations" value={formatNumber(overviewTotals.conversations)} subtitle="All chats" />
        <KpiCard title="Total Tokens" value={formatTokensMillions(overviewTotals.totalTokens)} subtitle="Prompt + completion" />
        <KpiCard title="Total Cost" value={formatCurrency(overviewTotals.totalCost)} subtitle="LLM spend" />
        <KpiCard title="Active Users" value={formatNumber(overviewTotals.uniqueUsers)} subtitle="Engaged in chats" />
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="card xl:col-span-2 p-5 md:p-6 shadow-lg">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-tqa-navy">Usage Over Time</h3>
              <p className="text-sm" style={{color:'rgba(255,255,255,0.5)'}}>Trendline of app usage and spend.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                value={timeMetric}
                onChange={(event) => setTimeMetric(event.target.value)}
                className="rounded-lg px-3 py-2 text-sm" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
              >
                <option value="tokens">Tokens</option>
                <option value="cost">Cost</option>
                <option value="conversations">Conversations</option>
              </select>
              <select
                value={granularity}
                onChange={(event) => setGranularity(event.target.value)}
                className="rounded-lg px-3 py-2 text-sm" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
              </select>
            </div>
          </div>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeSeries.series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="usageGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--tqa-border-dark)" />
                <XAxis dataKey="bucket" tickFormatter={formatDateLabel} tick={{ fill: 'rgba(255,255,255,0.6)' }} />
                <YAxis tickFormatter={(value) => (timeMetric === 'tokens' ? formatTokensMillions(value) : formatNumber(value))} tick={{ fill: 'rgba(255,255,255,0.6)' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--tqa-black)', borderColor: 'var(--tqa-border-dark)' }}
                  itemStyle={{ color: 'var(--tqa-white)' }}
                  labelStyle={{ color: 'var(--tqa-timberwolf)' }}
                  formatter={(value) =>
                    timeMetric === 'cost'
                      ? formatCurrency(value)
                      : timeMetric === 'tokens'
                        ? formatTokensMillions(value)
                        : formatNumber(value)
                  }
                  labelFormatter={formatDateLabel}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#3B82F6"
                  fill="url(#usageGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-5 md:p-6 shadow-lg flex flex-col">
          <h3 className="text-lg font-semibold text-tqa-navy">Model Mix</h3>
          <p className="text-sm" style={{color:'rgba(255,255,255,0.5)'}}>Click a slice to filter by model.</p>
          <div className="h-56 mt-4">
            {hasModelMixData ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={modelMixData}
                    dataKey="totalTokens"
                    nameKey="label"
                    innerRadius={50}
                    outerRadius={85}
                    onClick={(entry) => toggleFilter('modelName', entry.key)}
                  >
                    {modelMixData.map((entry, index) => (
                      <Cell key={entry.key} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatTokensMillions(value)} 
                    contentStyle={{ backgroundColor: 'var(--tqa-black)', borderColor: 'var(--tqa-border-dark)' }}
                    itemStyle={{ color: 'var(--tqa-white)' }}
                    labelStyle={{ color: 'var(--tqa-timberwolf)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm" style={{color:'rgba(255,255,255,0.4)'}}>
                No model usage for the selected filters.
              </div>
            )}
          </div>
          <div className="mt-3 max-h-24 overflow-y-auto pr-2">
            {modelMixData.map((entry, index) => (
              <div key={entry.key} className="flex items-center justify-between text-xs py-1" style={{color:'rgba(255,255,255,0.6)'}}>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: chartColors[index % chartColors.length] }}
                  />
                  <span className="truncate max-w-[140px]">{entry.label}</span>
                </div>
                <span style={{color:'rgba(255,255,255,0.45)'}}>{formatTokensMillions(entry.totalTokens)}</span>
              </div>
            ))}
          </div>
        </div>
        </section>

        <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card p-5 md:p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-tqa-navy">Top Users by Tokens</h3>
          <p className="text-sm" style={{color:'rgba(255,255,255,0.5)'}}>Click a bar to filter by user.</p>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={userBreakdown.items} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--tqa-border-dark)" />
                <XAxis type="number" tickFormatter={(value) => formatTokensMillions(value)} tick={{ fill: 'rgba(255,255,255,0.6)' }} />
                <YAxis type="category" dataKey="label" width={120} tick={{ fill: 'rgba(255,255,255,0.6)' }} />
                <Tooltip 
                  formatter={(value) => formatTokensMillions(value)} 
                  contentStyle={{ backgroundColor: 'var(--tqa-black)', borderColor: 'var(--tqa-border-dark)' }}
                  itemStyle={{ color: 'var(--tqa-white)' }}
                  labelStyle={{ color: 'var(--tqa-timberwolf)' }}
                />
                <Bar
                  dataKey="totalTokens"
                  fill="#0E3A8C"
                  radius={[6, 6, 6, 6]}
                  onClick={(data) => toggleFilter('userId', data.key)}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-5 md:p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-tqa-navy">Top Projects by Tokens</h3>
          <p className="text-sm" style={{color:'rgba(255,255,255,0.5)'}}>Click a bar to filter by project.</p>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={projectBreakdown.items} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--tqa-border-dark)" />
                <XAxis type="number" tickFormatter={(value) => formatTokensMillions(value)} tick={{ fill: 'rgba(255,255,255,0.6)' }} />
                <YAxis type="category" dataKey="label" width={140} tick={{ fill: 'rgba(255,255,255,0.6)' }} />
                <Tooltip 
                  formatter={(value) => formatTokensMillions(value)} 
                  contentStyle={{ backgroundColor: 'var(--tqa-black)', borderColor: 'var(--tqa-border-dark)' }}
                  itemStyle={{ color: 'var(--tqa-white)' }}
                  labelStyle={{ color: 'var(--tqa-timberwolf)' }}
                />
                <Bar
                  dataKey="totalTokens"
                  fill="#FF6A3D"
                  radius={[6, 6, 6, 6]}
                  onClick={(data) => toggleFilter('projectId', data.key)}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        </section>

        <section className="card p-5 md:p-6 shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-tqa-navy">Conversation Drilldown</h3>
            <p className="text-sm" style={{color:'rgba(255,255,255,0.5)'}}>Latest conversations with usage totals.</p>
          </div>
          <div className="text-xs" style={{color:'rgba(255,255,255,0.35)'}}>Last updated: {overview?.lastUpdated ? new Date(overview.lastUpdated).toLocaleString() : '—'}</div>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left" style={{color:'rgba(255,255,255,0.5)',borderBottom:'1px solid var(--tqa-border-dark)'}}>
                <th className="py-2 pr-4">Conversation</th>
                <th className="py-2 pr-4">User</th>
                <th className="py-2 pr-4">Project</th>
                <th className="py-2 pr-4">Tokens</th>
                <th className="py-2 pr-4">Cost</th>
                <th className="py-2 pr-4">Updated</th>
              </tr>
            </thead>
            <tbody>
              {conversations.items.map((row) => (
                <tr key={row.id} className="border-b last:border-b-0">
                  <td className="py-2 pr-4 font-medium" style={{color:'rgba(255,255,255,0.85)'}}>
                    {row.title || 'Conversation'}
                    {row.isCrossProject && (
                      <span className="ml-2 text-xs text-tqa-accent">Cross</span>
                    )}
                  </td>
                  <td className="py-2 pr-4" style={{color:'rgba(255,255,255,0.6)'}}>{row.userName}</td>
                  <td className="py-2 pr-4" style={{color:'rgba(255,255,255,0.6)'}}>{row.projectName}</td>
                  <td className="py-2 pr-4" style={{color:'rgba(255,255,255,0.7)'}}>{formatTokensMillions(row.totalTokens)}</td>
                  <td className="py-2 pr-4" style={{color:'rgba(255,255,255,0.7)'}}>{formatCurrency(row.totalCost)}</td>
                  <td className="py-2 pr-4" style={{color:'rgba(255,255,255,0.45)'}}>
                    {row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : ''}
                  </td>
                </tr>
              ))}
              {conversations.items.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center" style={{color:'rgba(255,255,255,0.4)'}}>
                    No conversations match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </section>
      </div>

      {filtersOpen && (
        <aside className="fixed right-6 top-40 bottom-20 z-40 w-72">
          <div className="card shadow-lg p-4 h-full overflow-y-auto">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-tqa-navy">Filters</h3>
                  <p className="text-[11px]" style={{color:'rgba(255,255,255,0.45)'}}>{activeFilterCount} active</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setRefreshToken((value) => value + 1)}
                  className="btn-primary px-2 py-1 text-xs"
                >
                  {loading ? 'Refreshing...' : 'Refresh Data'}
                </button>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="btn-secondary px-2 py-1 text-xs"
                >
                  Clear Filters
                </button>
              </div>

              <div className="flex flex-col gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(event) => handleFilterChange('startDate', event.target.value)}
                    className="mt-1 w-full rounded-md px-2 py-1 text-xs" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>End Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(event) => handleFilterChange('endDate', event.target.value)}
                    className="mt-1 w-full rounded-md px-2 py-1 text-xs" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>User</label>
                  <select
                    value={filters.userId}
                    onChange={(event) => handleFilterChange('userId', event.target.value)}
                    className="mt-1 w-full rounded-md px-2 py-1 text-xs" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  >
                    <option value="">All users</option>
                    {filterOptions.users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>Project</label>
                  <select
                    value={filters.projectId}
                    onChange={(event) => handleFilterChange('projectId', event.target.value)}
                    className="mt-1 w-full rounded-md px-2 py-1 text-xs" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  >
                    <option value="">All projects</option>
                    {filterOptions.projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>Folder</label>
                  <select
                    value={filters.folderId}
                    onChange={(event) => handleFilterChange('folderId', event.target.value)}
                    className="mt-1 w-full rounded-md px-2 py-1 text-xs" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  >
                    <option value="">All folders</option>
                    {filterOptions.folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>Model</label>
                  <select
                    value={filters.modelName}
                    onChange={(event) => handleFilterChange('modelName', event.target.value)}
                    className="mt-1 w-full rounded-md px-2 py-1 text-xs" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  >
                    <option value="">All models</option>
                    {filterOptions.models.map((model) => (
                      <option key={model.modelName} value={model.modelName}>
                        {model.displayName || model.modelName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)'}}>Cross-project</label>
                  <select
                    value={filters.isCrossProject}
                    onChange={(event) => handleFilterChange('isCrossProject', event.target.value)}
                    className="mt-1 w-full rounded-md px-2 py-1 text-xs" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  >
                    <option value="">All</option>
                    <option value="true">Cross-project only</option>
                    <option value="false">Project-scoped only</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </aside>
      )}

      <div className="fixed right-6 bottom-6 z-50">
        <button
          type="button"
          onClick={() => setFiltersOpen((value) => !value)}
          className={`btn-secondary p-2 shadow-lg relative${activeFilterCount > 0 ? ' ring-2 ring-tqa-accent' : ''}`}
          aria-label={filtersOpen ? 'Collapse filters' : 'Expand filters'}
        >
          {filtersOpen ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h18M6 12h12M10 19h4" />
            </svg>
          )}
          {activeFilterCount > 0 && (
            <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-tqa-accent text-white text-[10px] leading-[18px] text-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

const KpiCard = ({ title, value, subtitle }) => (
  <div className="card p-5 shadow-lg">
    <p className="text-xs uppercase tracking-wide" style={{color:'rgba(255,255,255,0.5)'}}>{title}</p>
    <p className="text-2xl font-semibold mt-2" style={{color:'rgba(255,255,255,0.9)'}}>{value}</p>
    <p className="text-xs mt-1" style={{color:'rgba(255,255,255,0.45)'}}>{subtitle}</p>
  </div>
);

export default AdminDashboard;
